import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { resolveSessionConfig } from "../modules/teleop-session";
import type { RawSessionConfig, ResolvedSessionConfig } from "../modules/teleop-session";

type AppConfigBase = {
  sessionId: string;
  status: "loading" | "error" | "ready";
  error: string | null;
};

/**
 * Discriminated result of `useAppConfig`. On `status: "ready"` the returned object
 * IS the resolved session config (its fields are spread at the top level), so consumers
 * read `config.field` directly — never `config.config.field`. This also drops the loose
 * `Record<string, unknown> | ResolvedSessionConfig | null` union entirely.
 *
 * Usage:
 * ```ts
 * const config = useAppConfig();
 * if (config.status === "loading") return …;
 * if (config.status === "error") return …;   // config.error is a string here
 * useOperatorSession(config.sessionId, config, …); // config is ResolvedSessionConfig here
 * ```
 */
export type UseAppConfigResult =
  | (AppConfigBase & { status: "loading"; error: null })
  | (AppConfigBase & { status: "error"; error: string })
  | (AppConfigBase & ResolvedSessionConfig & { status: "ready"; error: null });

export function useAppConfig(): UseAppConfigResult {
  const { sessionId: paramSessionId } = useParams();
  const sessionId = (paramSessionId || "").trim();

  const [config, setConfig] = useState<ResolvedSessionConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/config.json", { cache: "no-store" });
        if (!res.ok) throw new Error(`config.json: HTTP ${res.status}`);
        const data = (await res.json()) as RawSessionConfig;
        // Session/transport config assembly (incl. ephemeral credentials) is fully
        // handled by the teleop-session module — the app has no transport knowledge.
        const cfg = await resolveSessionConfig(data);
        if (!cancelled) {
          setConfig(cfg);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Memoised so the ready object identity is stable across unrelated re-renders.
  // This matters: useOperatorSession's lifecycle effect depends on the config reference,
  // and a fresh object on every render would restart the session each time.
  return useMemo<UseAppConfigResult>(() => {
    if (loading) {
      return { sessionId, status: "loading", error: null };
    }
    if (error) {
      return { sessionId, status: "error", error };
    }
    return {
      ...(config as ResolvedSessionConfig),
      sessionId,
      status: "ready",
      error: null,
    };
  }, [sessionId, config, error, loading]);
}
